/**
 * Environment utility functions
 */

/**
 * Determines if the current environment is production
 * Production is defined as any environment that doesn't start with 'stage.' or 'stage2.'
 * We can't use NODE_ENV since it's 'production' both on stage and prod
 * @returns {boolean} true if in production environment, false otherwise
 */
export const isProd = (): boolean => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;

  // Production is any environment that doesn't start with stage. or stage2.
  return (
    !hostname.startsWith('stage.') &&
    !hostname.startsWith('stage2.') &&
    hostname !== 'localhost' &&
    !hostname.startsWith('127.0.0.1') &&
    !hostname.startsWith('dev.') &&
    !hostname.includes('yad2.io')
  );
};

/**
 * Determines if the current environment is staging
 * @returns {boolean} true if in staging environment, false otherwise
 */
export const isStaging = (): boolean => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;

  return hostname.startsWith('stage.') || hostname.startsWith('stage2.');
};

/**
 * Determines if the current environment is localhost/development host
 */
export const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname.startsWith('127.0.0.1');
};

/**
 * Gets the current environment type
 * @returns {'production' | 'staging' | 'development'} the current environment
 */
export const getEnvironment = (): 'production' | 'staging' | 'development' => {
  if (typeof window === 'undefined') {
    return 'development';
  }

  const hostname = window.location.hostname;

  if (hostname.startsWith('stage.') || hostname.startsWith('stage2.')) {
    return 'staging';
  }

  if (isLocalhost()) {
    return 'development';
  }

  return 'production';
};

export default {
  isProd,
  isStaging,
  getEnvironment,
  isLocalhost,
};
